import {
    ASSISTANCE_PATH as INVOICE_ASSISTANCE_PATH,
    OVERVIEW_PATH as INVOICE_OVERVIEW_PATH,
} from '../products/invoices/constants';
import {
    ASSISTANCE_PATH as ORDER_ASSISTANCE_PATH,
    OVERVIEW_PATH as ORDER_OVERVIEW_PATH,
} from '../products/orders/constants';
import {
    ASSISTANCE_PATH as ORDER_CONFIRMATION_ASSISTANCE_PATH,
    OVERVIEW_PATH as ORDER_CONFIRMATION_OVERVIEW_PATH,
} from '../products/order_confirmations/constants';
import { ASSISTANCE_PATH as RFQ_ASSISTANCE_PATH, OVERVIEW_PATH as RFQ_OVERVIEW_PATH } from '../products/rfqs/constants';
import { OVERVIEW_PATH as PROPERTY_BILL_OVERVIEW_PATH } from '../products/db_immo/constants';
import {
    ASSISTANCE_PATH as DELIVERY_NOTE_ASSISTANCE_PATH,
    OVERVIEW_PATH as DELIVERY_NOTE_OVERVIEW_PATH,
} from '../products/delivery_notes/constants';
import { OVERVIEW_PATH as UNIVERSAL_OVERVIEW_PATH } from '../products/universal/constants';
import { OVERVIEW_PATH as LIST_OF_SERVICES_OVERVIEW_PATH } from '../products/list_of_services/constants';
import { capitalizeFirstLetter } from '../core/utils/string.ts';
import { camelCase } from 'lodash';

export enum DocumentType {
    Order,
    OrderConfirmation,
    Invoice,
    Rfq,
    PropertyBill,
    DeliveryNote,
    Universal,
    ListOfServices,
}

export const OVERVIEW_PATHS = {
    [DocumentType.Order]: ORDER_OVERVIEW_PATH,
    [DocumentType.OrderConfirmation]: ORDER_CONFIRMATION_OVERVIEW_PATH,
    [DocumentType.Invoice]: INVOICE_OVERVIEW_PATH,
    [DocumentType.Rfq]: RFQ_OVERVIEW_PATH,
    [DocumentType.PropertyBill]: PROPERTY_BILL_OVERVIEW_PATH,
    [DocumentType.DeliveryNote]: DELIVERY_NOTE_OVERVIEW_PATH,
    [DocumentType.Universal]: UNIVERSAL_OVERVIEW_PATH,
    [DocumentType.ListOfServices]: LIST_OF_SERVICES_OVERVIEW_PATH,
};

// the document types the nightly analytics sync covers, so their field config can show why documents needed assistance
export const FIELD_ASSISTANCE_PATHS: Partial<Record<DocumentType, string>> = {
    [DocumentType.Order]: ORDER_ASSISTANCE_PATH,
    [DocumentType.OrderConfirmation]: ORDER_CONFIRMATION_ASSISTANCE_PATH,
    [DocumentType.Invoice]: INVOICE_ASSISTANCE_PATH,
    [DocumentType.Rfq]: RFQ_ASSISTANCE_PATH,
    [DocumentType.DeliveryNote]: DELIVERY_NOTE_ASSISTANCE_PATH,
};

export const getDocumentType = (document?: any): DocumentType =>
    DocumentType[capitalizeFirstLetter(camelCase(document.__typename).replace('_', '')) as keyof typeof DocumentType];

export enum LabelingStatus {
    NotEligible,
    Eligible,
    Unknown,
}

export const getLabelingStatus = (record: any): LabelingStatus =>
    LabelingStatus[
        capitalizeFirstLetter(camelCase(record.labelingStatus).replace('_', '')) as keyof typeof LabelingStatus
    ];

export enum DOCUMENT_TYPE {
    ORDER = 'ORDER',
    ORDER_CONFIRMATION = 'ORDER_CONFIRMATION',
    REQUEST_FOR_QUOTATION = 'REQUEST_FOR_QUOTATION',
    INVOICE = 'INVOICE',
    DELIVERY_NOTE = 'DELIVERY_NOTE',
    PROPERTY_BILL = 'PROPERTY_BILL',
    LIST_OF_SERVICES = 'LIST_OF_SERVICES',
    UNIVERSAL = 'UNIVERSAL',
}

export const documentTypeClassificationI18nKey: Record<DocumentType, string> = {
    [DocumentType.Order]: 'attachments.classification.order',
    [DocumentType.OrderConfirmation]: 'attachments.classification.order_confirmation',
    [DocumentType.Invoice]: 'attachments.classification.invoice',
    [DocumentType.Rfq]: 'attachments.classification.request_for_quotation',
    [DocumentType.PropertyBill]: 'attachments.classification.property_bill',
    [DocumentType.DeliveryNote]: 'attachments.classification.delivery_note',
    [DocumentType.Universal]: 'attachments.classification.universal',
    [DocumentType.ListOfServices]: 'attachments.classification.list_of_services',
};

export const documentTypeToKey: Record<DocumentType, string> = {
    [DocumentType.Order]: 'order',
    [DocumentType.OrderConfirmation]: 'order_confirmation',
    [DocumentType.Invoice]: 'invoice',
    [DocumentType.Rfq]: 'request_for_quotation',
    [DocumentType.PropertyBill]: 'property_bill',
    [DocumentType.DeliveryNote]: 'delivery_note',
    [DocumentType.Universal]: 'universal',
    [DocumentType.ListOfServices]: 'list_of_services',
};

export const mapDocumentTypeToGqlDocumentType: Record<DocumentType, DOCUMENT_TYPE> = {
    [DocumentType.Order]: DOCUMENT_TYPE.ORDER,
    [DocumentType.OrderConfirmation]: DOCUMENT_TYPE.ORDER_CONFIRMATION,
    [DocumentType.Invoice]: DOCUMENT_TYPE.INVOICE,
    [DocumentType.Rfq]: DOCUMENT_TYPE.REQUEST_FOR_QUOTATION,
    [DocumentType.PropertyBill]: DOCUMENT_TYPE.PROPERTY_BILL,
    [DocumentType.DeliveryNote]: DOCUMENT_TYPE.DELIVERY_NOTE,
    [DocumentType.Universal]: DOCUMENT_TYPE.UNIVERSAL,
    [DocumentType.ListOfServices]: DOCUMENT_TYPE.LIST_OF_SERVICES,
};

export const DEFAULT_LINE_ITEM_TYPE = 'line_items';

export const REPLACEMENT_WEEKDAY_OPTIONS = [
    { value: 0, labelKey: 'configPage.document.form.dayNames.monday' },
    { value: 1, labelKey: 'configPage.document.form.dayNames.tuesday' },
    { value: 2, labelKey: 'configPage.document.form.dayNames.wednesday' },
    { value: 3, labelKey: 'configPage.document.form.dayNames.thursday' },
    { value: 4, labelKey: 'configPage.document.form.dayNames.friday' },
];

export const DOCUMENT_NUMBER_FALLBACK_OPTIONS = [
    { value: 'EMAIL_SUBJECT', labelKey: 'configPage.document.form.documentNumberFallbackOptions.emailSubject' },
    { value: 'EMAIL_TIMESTAMP', labelKey: 'configPage.document.form.documentNumberFallbackOptions.emailTimestamp' },
    { value: 'CURRENT_DATE', labelKey: 'configPage.document.form.documentNumberFallbackOptions.currentDate' },
];

export const CLASSIFICATION_OPTIONS = [
    'order',
    'request_for_quotation',
    'order_confirmation',
    'invoice',
    'delivery_note',
    'offer',
    'other',
];

export const mapDocumentTypeNameToGqlDocumentType: Record<string, DOCUMENT_TYPE> = {
    order: DOCUMENT_TYPE.ORDER,
    orderConfirmation: DOCUMENT_TYPE.ORDER_CONFIRMATION,
    rfq: DOCUMENT_TYPE.REQUEST_FOR_QUOTATION,
    invoice: DOCUMENT_TYPE.INVOICE,
    deliveryNote: DOCUMENT_TYPE.DELIVERY_NOTE,
    propertyBill: DOCUMENT_TYPE.PROPERTY_BILL,
    universal: DOCUMENT_TYPE.UNIVERSAL,
    listOfServices: DOCUMENT_TYPE.LIST_OF_SERVICES,
};

export enum ConnectionStatus {
    Connected = 'Connected',
    Paused = 'Paused',
    Disconnected = 'Disconnected',
    Error = 'Error',
}

export enum SmartInboxConnectionStatus {
    Connected = 'ENABLED',
    Paused = 'DISABLED',
    Disconnected = 'NOT_CONNECTED',
}

export enum ConnectionAction {
    Pause = 'Pause',
    Resume = 'Resume',
    Disconnect = 'Disconnect',
}

export const getAvailableActions = (status: ConnectionStatus): ConnectionAction[] => {
    switch (status) {
        case ConnectionStatus.Connected:
            return [ConnectionAction.Pause, ConnectionAction.Disconnect];
        case ConnectionStatus.Paused:
            return [ConnectionAction.Resume, ConnectionAction.Disconnect];
        case ConnectionStatus.Error:
            // the stored Outlook sign-in no longer works; only disconnecting and connecting again restores it
            return [ConnectionAction.Disconnect];
        case ConnectionStatus.Disconnected:
            return [];
        default:
            return [];
    }
};

/** A broken Outlook connection outranks the pause toggle: resuming would not bring emails back. */
export const getPersonalSmartInboxConnectionStatus = (
    integration?: { enabled: boolean; connectionFailedAt?: string | null } | null
): ConnectionStatus => {
    if (!integration) return ConnectionStatus.Disconnected;
    if (integration.connectionFailedAt) return ConnectionStatus.Error;
    return integration.enabled ? ConnectionStatus.Connected : ConnectionStatus.Paused;
};

export const emailsAndDomainsRegex =
    /^(@?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}|[^\s@]+@[^\s@]+\.[^\s@]+)$/;
export const ValidationCheckResultStatus = {
    PASSED: 'check_passed',
    INCOMPLETE: 'data_incomplete',
    FAILED: 'check_failed',
};

export const UserFilterName = {
    ASSIGNED_TO: 'assigned_to',
    CREATED_BY: 'created_by',
    ASSISTED_BY: 'assisted_by',
    CREATED_SOURCE: 'created_source',
    CREATED_AT: 'created_at',
    FINISHED_AT: 'finished_at',
    CLIENT: 'client',
    CLIENT_TYPE: 'client_type',
    ORDER_BY: 'order_by',
    SEARCH: 'search',
};

export const PROCESSING_STATUS_COLOR_CLASS = {
    PENDING: '',
    ON_HOLD: '',
    ASSISTANCE_NEEDED: 'warning',
    ASSISTED: '',
    FINISHED: 'success',
    DELIVERED: 'success',
    IMPORTED: 'success',
    FAILED: 'error',
    DISCARDED: '',
    REJECTED: 'error',
    FINISHED_TESTING: 'success',
    DELETED: '',
};
